/**
 * Audit Trail Export Utilities
 * 
 * Converts audit trail JSON to enterprise-facing DOCX and Markdown documents
 * Structured templates that show all data without requiring AI generation
 */

interface AuditTrailData {
  export_metadata?: any;
  artifact_to_entity_mapping?: any[];
  artifacts?: any[];
  job_statistics?: any;
  data_pipeline_methodology?: any;
  policy_pack_details?: any;
  entity_statistics?: any;
  sources?: any[];
  pfas_specific_details?: any;
}

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes?: number): string {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Format duration in seconds to human-readable string
 */
function formatDuration(seconds?: number): string {
  if (!seconds) return 'N/A';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(2)}h`;
}

/**
 * Format date string to readable format
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

/**
 * Escape markdown special characters
 */
function escapeMarkdown(text: string): string {
  return text.replace(/[#*_`\[\]()\\]/g, '\\$&');
}

/**
 * Convert audit trail JSON to Markdown format
 */
export function exportAuditTrailToMarkdown(data: AuditTrailData, filename: string = 'audit_trail.md'): void {
  const sections: string[] = [];

  // Title Page
  sections.push('# Audit Trail Report');
  sections.push('');
  
  if (data.export_metadata) {
    const meta = data.export_metadata;
    sections.push(`**Project:** ${meta.project_name || meta.project_id || 'N/A'}`);
    sections.push(`**Export Date:** ${formatDate(meta.export_date)}`);
    sections.push(`**Total Entities:** ${meta.total_entities?.toLocaleString() || 0}`);
    sections.push(`**Total Artifacts:** ${meta.total_artifacts?.toLocaleString() || 0}`);
    sections.push(`**Total Jobs:** ${meta.total_jobs?.toLocaleString() || 0}`);
    if (meta.entity_types && meta.entity_types.length > 0) {
      sections.push(`**Entity Types:** ${meta.entity_types.join(', ')}`);
    }
    sections.push('');
    sections.push('---');
    sections.push('');
  }

  // Table of Contents
  sections.push('## Table of Contents');
  sections.push('');
  sections.push('1. [Export Metadata](#export-metadata)');
  sections.push('2. [Entity Statistics](#entity-statistics)');
  sections.push('3. [Artifact to Entity Mapping](#artifact-to-entity-mapping)');
  sections.push('4. [Job Statistics](#job-statistics)');
  sections.push('5. [Data Pipeline Methodology](#data-pipeline-methodology)');
  if (data.policy_pack_details) {
    sections.push('6. [Policy Pack Details](#policy-pack-details)');
  }
  if (data.sources && data.sources.length > 0) {
    sections.push('7. [Data Sources](#data-sources)');
  }
  if (data.artifacts && data.artifacts.length > 0) {
    sections.push('8. [Artifacts Detail](#artifacts-detail)');
  }
  if (data.pfas_specific_details) {
    sections.push('9. [PFAS-Specific Details](#pfas-specific-details)');
  }
  sections.push('');
  sections.push('---');
  sections.push('');

  // 1. Export Metadata
  sections.push('## 1. Export Metadata');
  sections.push('');
  if (data.export_metadata) {
    const meta = data.export_metadata;
    sections.push('| Field | Value |');
    sections.push('|-------|-------|');
    sections.push(`| Export Date | ${formatDate(meta.export_date)} |`);
    sections.push(`| Project ID | ${meta.project_id || 'N/A'} |`);
    sections.push(`| Project Name | ${meta.project_name || 'N/A'} |`);
    sections.push(`| Total Entities | ${meta.total_entities?.toLocaleString() || 0} |`);
    sections.push(`| Total Artifacts | ${meta.total_artifacts?.toLocaleString() || 0} |`);
    sections.push(`| Total Jobs | ${meta.total_jobs?.toLocaleString() || 0} |`);
    if (meta.entity_types && meta.entity_types.length > 0) {
      sections.push(`| Entity Types | ${meta.entity_types.join(', ')} |`);
    }
    sections.push('');
  }

  // 2. Entity Statistics
  sections.push('## 2. Entity Statistics');
  sections.push('');
  if (data.entity_statistics) {
    const stats = data.entity_statistics;
    
    if (stats.by_type) {
      sections.push('### Entities by Type');
      sections.push('');
      sections.push('| Entity Type | Count |');
      sections.push('|-------------|-------|');
      Object.entries(stats.by_type).forEach(([type, count]) => {
        sections.push(`| ${escapeMarkdown(type)} | ${(count as number).toLocaleString()} |`);
      });
      sections.push('');
    }
    
    sections.push('### Traceability Summary');
    sections.push('');
    sections.push('| Metric | Count |');
    sections.push('|--------|-------|');
    sections.push(`| Entities with Artifact Trace | ${stats.with_artifact_trace?.toLocaleString() || 0} |`);
    sections.push(`| Entities without Artifact Trace | ${stats.without_artifact_trace?.toLocaleString() || 0} |`);
    sections.push('');
  }

  // 3. Artifact to Entity Mapping
  sections.push('## 3. Artifact to Entity Mapping');
  sections.push('');
  if (data.artifact_to_entity_mapping && data.artifact_to_entity_mapping.length > 0) {
    sections.push('| Artifact ID | Filename | Type | Size | Entity Count |');
    sections.push('|-------------|----------|------|------|--------------|');
    data.artifact_to_entity_mapping.forEach((mapping) => {
      sections.push(
        `| ${mapping.artifact_id} | ${escapeMarkdown(mapping.filename || 'N/A')} | ${mapping.artifact_type || 'N/A'} | ${formatBytes(mapping.file_size_bytes)} | ${mapping.entity_ids?.length || 0} |`
      );
    });
    sections.push('');
    
    // Detailed mapping
    sections.push('### Detailed Entity Mapping');
    sections.push('');
    data.artifact_to_entity_mapping.forEach((mapping, idx) => {
      sections.push(`#### ${idx + 1}. ${escapeMarkdown(mapping.filename || 'Unknown Artifact')}`);
      sections.push('');
      sections.push(`- **Artifact ID:** ${mapping.artifact_id}`);
      sections.push(`- **Type:** ${mapping.artifact_type || 'N/A'}`);
      sections.push(`- **Size:** ${formatBytes(mapping.file_size_bytes)}`);
      sections.push(`- **SHA256:** ${mapping.sha256 || 'N/A'}`);
      sections.push(`- **Source Type:** ${mapping.source_type || 'N/A'}`);
      sections.push(`- **Created At:** ${formatDate(mapping.created_at)}`);
      sections.push(`- **Entity Count:** ${mapping.entity_ids?.length || 0}`);
      if (mapping.entity_ids && mapping.entity_ids.length > 0) {
        sections.push('');
        sections.push('**Entity IDs:**');
        sections.push('```');
        mapping.entity_ids.forEach((id: string) => {
          sections.push(id);
        });
        sections.push('```');
      }
      sections.push('');
    });
  }

  // 4. Job Statistics
  sections.push('## 4. Job Statistics');
  sections.push('');
  if (data.job_statistics) {
    const stats = data.job_statistics;
    
    sections.push('### Summary');
    sections.push('');
    sections.push('| Metric | Value |');
    sections.push('|--------|-------|');
    sections.push(`| Total Jobs | ${stats.total_jobs?.toLocaleString() || 0} |`);
    sections.push(`| Total Chunks Created | ${stats.total_chunks_created?.toLocaleString() || 0} |`);
    sections.push(`| Total Entities Created | ${stats.total_entities_created?.toLocaleString() || 0} |`);
    sections.push(`| Total Evidence Entries | ${stats.total_evidence_entries?.toLocaleString() || 0} |`);
    sections.push(`| Total Processing Time | ${formatDuration(stats.total_processing_time_seconds)} |`);
    if (stats.average_processing_time_seconds) {
      sections.push(`| Average Processing Time | ${formatDuration(stats.average_processing_time_seconds)} |`);
    }
    sections.push('');
    
    if (stats.jobs && stats.jobs.length > 0) {
      sections.push('### Job Details');
      sections.push('');
      stats.jobs.forEach((job: any, idx: number) => {
        sections.push(`#### Job ${idx + 1}: ${job.job_type || 'ingest'}`);
        sections.push('');
        sections.push(`- **Job ID:** ${job.job_id}`);
        sections.push(`- **Status:** ${job.job_status || 'N/A'}`);
        sections.push(`- **Created At:** ${formatDate(job.created_at)}`);
        sections.push(`- **Started At:** ${formatDate(job.started_at)}`);
        sections.push(`- **Completed At:** ${formatDate(job.completed_at)}`);
        sections.push(`- **Processing Time:** ${formatDuration(job.processing_time_seconds)}`);
        sections.push(`- **Artifacts Processed:** ${job.num_artifacts_processed || 0}`);
        sections.push(`- **Chunks Created:** ${job.num_chunks_created || 0}`);
        sections.push(`- **Entities Created:** ${job.total_entities_created || 0}`);
        if (job.chunking_strategy) {
          sections.push(`- **Chunking Strategy:** ${job.chunking_strategy}`);
        }
        if (job.index_types && job.index_types.length > 0) {
          sections.push(`- **Index Types:** ${job.index_types.join(', ')}`);
        }
        if (job.index_classifications && job.index_classifications.length > 0) {
          sections.push(`- **Classifications:** ${job.index_classifications.join(', ')}`);
        }
        sections.push('');
      });
    }
  }

  // 5. Data Pipeline Methodology
  sections.push('## 5. Data Pipeline Methodology');
  sections.push('');
  if (data.data_pipeline_methodology) {
    const methodology = data.data_pipeline_methodology;
    
    if (methodology.chunking_strategy) {
      sections.push('### Chunking Strategy');
      sections.push('');
      const chunking = methodology.chunking_strategy;
      sections.push(`- **Primary Strategy:** ${chunking.primary_strategy || 'N/A'}`);
      if (chunking.strategies_used && chunking.strategies_used.length > 0) {
        sections.push(`- **Strategies Used:** ${chunking.strategies_used.join(', ')}`);
      }
      sections.push(`- **Description:** ${chunking.description || 'N/A'}`);
      if (chunking.classification_summary) {
        sections.push('');
        sections.push('**Classification Summary:**');
        Object.entries(chunking.classification_summary).forEach(([key, value]) => {
          sections.push(`- ${key.replace(/_/g, ' ')}: ${value ? 'Yes' : 'No'}`);
        });
      }
      sections.push('');
    }
    
    if (methodology.entity_extraction_methodology) {
      sections.push('### Entity Extraction Methodology');
      sections.push('');
      const extraction = methodology.entity_extraction_methodology;
      sections.push(`- **Approach:** ${extraction.approach || 'N/A'}`);
      sections.push(`- **Validation:** ${extraction.validation || 'N/A'}`);
      sections.push('');
    }
    
    if (methodology.processing_approach) {
      sections.push('### Processing Approach');
      sections.push('');
      const processing = methodology.processing_approach;
      sections.push(`- **Description:** ${processing.description || 'N/A'}`);
      if (processing.average_processing_time_seconds) {
        sections.push(`- **Average Processing Time:** ${formatDuration(processing.average_processing_time_seconds)}`);
      }
      sections.push('');
    }
    
    if (methodology.quality_measures) {
      sections.push('### Quality Measures');
      sections.push('');
      const quality = methodology.quality_measures;
      if (quality.validation_protocols && quality.validation_protocols.length > 0) {
        sections.push('**Validation Protocols:**');
        quality.validation_protocols.forEach((protocol: any) => {
          sections.push(`- ${JSON.stringify(protocol)}`);
        });
        sections.push('');
      }
      if (quality.warnings && quality.warnings.length > 0) {
        sections.push('**Warnings:**');
        quality.warnings.forEach((warning: string) => {
          sections.push(`- ⚠️ ${warning}`);
        });
        sections.push('');
      }
    }
  }

  // 6. Policy Pack Details
  if (data.policy_pack_details) {
    sections.push('## 6. Policy Pack Details');
    sections.push('');
    const policy = data.policy_pack_details;
    sections.push(`- **ID:** ${policy.id || 'N/A'}`);
    sections.push(`- **Name:** ${policy.name || 'N/A'}`);
    sections.push(`- **Version:** ${policy.version || 'N/A'}`);
    if (policy.description) {
      sections.push(`- **Description:** ${policy.description}`);
    }
    sections.push(`- **Created At:** ${formatDate(policy.created_at)}`);
    sections.push(`- **Updated At:** ${formatDate(policy.updated_at)}`);
    sections.push('');
    
    if (policy.rules) {
      sections.push('### Policy Pack Rules');
      sections.push('');
      sections.push('```json');
      sections.push(JSON.stringify(policy.rules, null, 2));
      sections.push('```');
      sections.push('');
    }
  }

  // 7. Data Sources
  if (data.sources && data.sources.length > 0) {
    sections.push('## 7. Data Sources');
    sections.push('');
    sections.push('| Source ID | Name | Type | Artifacts | Created At |');
    sections.push('|-----------|------|------|-----------|------------|');
    data.sources.forEach((source) => {
      sections.push(
        `| ${source.source_id} | ${escapeMarkdown(source.name || 'N/A')} | ${source.source_type || 'N/A'} | ${source.num_artifacts || 0} | ${formatDate(source.created_at)} |`
      );
    });
    sections.push('');
  }

  // 8. Artifacts Detail
  if (data.artifacts && data.artifacts.length > 0) {
    sections.push('## 8. Artifacts Detail');
    sections.push('');
    data.artifacts.forEach((artifact, idx) => {
      sections.push(`### ${idx + 1}. ${escapeMarkdown(artifact.filename || 'Unknown')}`);
      sections.push('');
      sections.push(`- **Artifact ID:** ${artifact.artifact_id}`);
      sections.push(`- **Type:** ${artifact.artifact_type || 'N/A'}`);
      sections.push(`- **Size:** ${formatBytes(artifact.file_size_bytes)}`);
      sections.push(`- **SHA256:** ${artifact.sha256 || 'N/A'}`);
      sections.push(`- **Source ID:** ${artifact.source_id || 'N/A'}`);
      sections.push(`- **Source Name:** ${artifact.source_name || 'N/A'}`);
      sections.push(`- **Source Type:** ${artifact.source_type || 'N/A'}`);
      sections.push(`- **Chunks:** ${artifact.num_chunks || 0}`);
      sections.push(`- **Entities:** ${artifact.num_entities || 0}`);
      sections.push(`- **Created At:** ${formatDate(artifact.created_at)}`);
      
      if (artifact.chunks && artifact.chunks.length > 0) {
        sections.push('');
        sections.push('**Chunks:**');
        sections.push('');
        sections.push('| Chunk ID | Strategy | Position | Content Length |');
        sections.push('|----------|----------|----------|----------------|');
        artifact.chunks.forEach((chunk: any) => {
          sections.push(
            `| ${chunk.chunk_id} | ${chunk.chunk_strategy || 'N/A'} | ${chunk.position || 0} | ${chunk.content_length || 0} |`
          );
        });
      }
      sections.push('');
    });
  }

  // 9. PFAS-Specific Details
  if (data.pfas_specific_details) {
    sections.push('## 9. PFAS-Specific Details');
    sections.push('');
    sections.push('```json');
    sections.push(JSON.stringify(data.pfas_specific_details, null, 2));
    sections.push('```');
    sections.push('');
  }

  // Footer
  sections.push('---');
  sections.push('');
  sections.push(`*Report generated on ${formatDate(new Date().toISOString())}*`);
  sections.push('');

  // Create and download
  const markdownContent = sections.join('\n');
  const blob = new Blob([markdownContent], { type: 'text/markdown;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

/**
 * Convert audit trail JSON to DOCX format
 * Uses docx library for structured document generation
 */
export async function exportAuditTrailToDOCX(
  data: AuditTrailData,
  filename: string = 'audit_trail.docx'
): Promise<void> {
  // Dynamic import to avoid loading docx if not needed
  let docxModule;
  try {
    docxModule = await import('docx');
  } catch (error) {
    throw new Error('DOCX export requires the "docx" package. Please install it: pnpm add docx');
  }
  
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, AlignmentType } = docxModule;

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          // Title
          new Paragraph({
            text: 'Audit Trail Report',
            heading: HeadingLevel.TITLE,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          }),

          // Export Metadata Section
          ...(data.export_metadata ? [
            new Paragraph({
              text: '1. Export Metadata',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            new Table({
              columnWidths: [3000, 5000],
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Field')] }),
                    new TableCell({ children: [new Paragraph('Value')] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Export Date')] }),
                    new TableCell({ children: [new Paragraph(formatDate(data.export_metadata.export_date))] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Project Name')] }),
                    new TableCell({ children: [new Paragraph(data.export_metadata.project_name || 'N/A')] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Entities')] }),
                    new TableCell({ children: [new Paragraph((data.export_metadata.total_entities || 0).toLocaleString())] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Artifacts')] }),
                    new TableCell({ children: [new Paragraph((data.export_metadata.total_artifacts || 0).toLocaleString())] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Jobs')] }),
                    new TableCell({ children: [new Paragraph((data.export_metadata.total_jobs || 0).toLocaleString())] }),
                  ],
                }),
              ],
            }),
            new Paragraph({ text: '' }),
          ] : []),

          // Entity Statistics Section
          ...(data.entity_statistics ? [
            new Paragraph({
              text: '2. Entity Statistics',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            new Paragraph({
              text: 'Entities by Type',
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 },
            }),
            new Table({
              columnWidths: [5000, 2000],
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Entity Type')] }),
                    new TableCell({ children: [new Paragraph('Count')] }),
                  ],
                }),
                ...(data.entity_statistics.by_type
                  ? Object.entries(data.entity_statistics.by_type).map(([type, count]) =>
                      new TableRow({
                        children: [
                          new TableCell({ children: [new Paragraph(type)] }),
                          new TableCell({ children: [new Paragraph((count as number).toLocaleString())] }),
                        ],
                      })
                    )
                  : []),
              ],
            }),
            new Paragraph({ text: '' }),
          ] : []),

          // Job Statistics Section
          ...(data.job_statistics ? [
            new Paragraph({
              text: '3. Job Statistics',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            new Table({
              columnWidths: [3000, 5000],
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Metric')] }),
                    new TableCell({ children: [new Paragraph('Value')] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Jobs')] }),
                    new TableCell({ children: [new Paragraph((data.job_statistics.total_jobs || 0).toLocaleString())] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Chunks Created')] }),
                    new TableCell({ children: [new Paragraph((data.job_statistics.total_chunks_created || 0).toLocaleString())] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Entities Created')] }),
                    new TableCell({ children: [new Paragraph((data.job_statistics.total_entities_created || 0).toLocaleString())] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Processing Time')] }),
                    new TableCell({ children: [new Paragraph(formatDuration(data.job_statistics.total_processing_time_seconds))] }),
                  ],
                }),
              ],
            }),
            new Paragraph({ text: '' }),
          ] : []),

          // Artifact to Entity Mapping
          ...(data.artifact_to_entity_mapping && data.artifact_to_entity_mapping.length > 0 ? [
            new Paragraph({
              text: '3. Artifact to Entity Mapping',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            new Table({
              columnWidths: [2000, 3000, 1500, 1500, 2000],
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Artifact ID')] }),
                    new TableCell({ children: [new Paragraph('Filename')] }),
                    new TableCell({ children: [new Paragraph('Type')] }),
                    new TableCell({ children: [new Paragraph('Size')] }),
                    new TableCell({ children: [new Paragraph('Entity Count')] }),
                  ],
                }),
                ...data.artifact_to_entity_mapping.map((mapping) =>
                  new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph(mapping.artifact_id.substring(0, 8) + '...')] }),
                      new TableCell({ children: [new Paragraph(mapping.filename || 'N/A')] }),
                      new TableCell({ children: [new Paragraph(mapping.artifact_type || 'N/A')] }),
                      new TableCell({ children: [new Paragraph(formatBytes(mapping.file_size_bytes))] }),
                      new TableCell({ children: [new Paragraph((mapping.entity_ids?.length || 0).toString())] }),
                    ],
                  })
                ),
              ],
            }),
            new Paragraph({ text: '' }),
          ] : []),

          // Job Statistics Section (renumbered)
          ...(data.job_statistics ? [
            new Paragraph({
              text: '4. Job Statistics',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            new Paragraph({
              text: 'Summary',
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 200, after: 100 },
            }),
            new Table({
              columnWidths: [3000, 5000],
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Metric')] }),
                    new TableCell({ children: [new Paragraph('Value')] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Jobs')] }),
                    new TableCell({ children: [new Paragraph((data.job_statistics.total_jobs || 0).toLocaleString())] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Chunks Created')] }),
                    new TableCell({ children: [new Paragraph((data.job_statistics.total_chunks_created || 0).toLocaleString())] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Entities Created')] }),
                    new TableCell({ children: [new Paragraph((data.job_statistics.total_entities_created || 0).toLocaleString())] }),
                  ],
                }),
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Total Processing Time')] }),
                    new TableCell({ children: [new Paragraph(formatDuration(data.job_statistics.total_processing_time_seconds))] }),
                  ],
                }),
              ],
            }),
            ...(data.job_statistics.jobs && data.job_statistics.jobs.length > 0 ? [
              new Paragraph({
                text: 'Job Details',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 300, after: 100 },
              }),
              ...data.job_statistics.jobs.flatMap((job: any, idx: number) => [
                new Paragraph({
                  text: `Job ${idx + 1}: ${job.job_type || 'ingest'}`,
                  heading: HeadingLevel.HEADING_3,
                  spacing: { before: 200, after: 100 },
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'Job ID: ', bold: true }),
                    new TextRun({ text: job.job_id }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'Status: ', bold: true }),
                    new TextRun({ text: job.job_status || 'N/A' }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'Processing Time: ', bold: true }),
                    new TextRun({ text: formatDuration(job.processing_time_seconds) }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'Chunks Created: ', bold: true }),
                    new TextRun({ text: (job.num_chunks_created || 0).toString() }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'Entities Created: ', bold: true }),
                    new TextRun({ text: (job.total_entities_created || 0).toString() }),
                  ],
                }),
                ...(job.chunking_strategy ? [
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Chunking Strategy: ', bold: true }),
                      new TextRun({ text: job.chunking_strategy }),
                    ],
                  }),
                ] : []),
                new Paragraph({ text: '' }),
              ]),
            ] : []),
            new Paragraph({ text: '' }),
          ] : []),

          // Data Pipeline Methodology (renumbered)
          ...(data.data_pipeline_methodology ? [
            new Paragraph({
              text: '5. Data Pipeline Methodology',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            ...(data.data_pipeline_methodology.chunking_strategy ? [
              new Paragraph({
                text: 'Chunking Strategy',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Primary Strategy: ', bold: true }),
                  new TextRun({ text: data.data_pipeline_methodology.chunking_strategy.primary_strategy || 'N/A' }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Description: ', bold: true }),
                  new TextRun({ text: data.data_pipeline_methodology.chunking_strategy.description || 'N/A' }),
                ],
              }),
              ...(data.data_pipeline_methodology.chunking_strategy.classification_summary ? [
                new Paragraph({ text: 'Classification Summary:', spacing: { before: 100 } }),
                ...Object.entries(data.data_pipeline_methodology.chunking_strategy.classification_summary).map(([key, value]) =>
                  new Paragraph({
                    children: [
                      new TextRun({ text: `  • ${key.replace(/_/g, ' ')}: ` }),
                      new TextRun({ text: value ? 'Yes' : 'No' }),
                    ],
                  })
                ),
              ] : []),
              new Paragraph({ text: '' }),
            ] : []),
            ...(data.data_pipeline_methodology.entity_extraction_methodology ? [
              new Paragraph({
                text: 'Entity Extraction Methodology',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Approach: ', bold: true }),
                  new TextRun({ text: data.data_pipeline_methodology.entity_extraction_methodology.approach || 'N/A' }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Validation: ', bold: true }),
                  new TextRun({ text: data.data_pipeline_methodology.entity_extraction_methodology.validation || 'N/A' }),
                ],
              }),
              new Paragraph({ text: '' }),
            ] : []),
          ] : []),

          // Policy Pack Details (renumbered)
          ...(data.policy_pack_details ? [
            new Paragraph({
              text: '6. Policy Pack Details',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Name: ', bold: true }),
                new TextRun({ text: data.policy_pack_details.name || 'N/A' }),
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: 'Version: ', bold: true }),
                new TextRun({ text: data.policy_pack_details.version || 'N/A' }),
              ],
            }),
            ...(data.policy_pack_details.description ? [
              new Paragraph({
                children: [
                  new TextRun({ text: 'Description: ', bold: true }),
                  new TextRun({ text: data.policy_pack_details.description }),
                ],
              }),
            ] : []),
            new Paragraph({
              children: [
                new TextRun({ text: 'Created At: ', bold: true }),
                new TextRun({ text: formatDate(data.policy_pack_details.created_at) }),
              ],
            }),
            new Paragraph({ text: '' }),
          ] : []),

          // Data Sources (renumbered)
          ...(data.sources && data.sources.length > 0 ? [
            new Paragraph({
              text: '7. Data Sources',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            new Table({
              columnWidths: [2000, 3000, 2000, 1500, 1500],
              rows: [
                new TableRow({
                  children: [
                    new TableCell({ children: [new Paragraph('Source ID')] }),
                    new TableCell({ children: [new Paragraph('Name')] }),
                    new TableCell({ children: [new Paragraph('Type')] }),
                    new TableCell({ children: [new Paragraph('Artifacts')] }),
                    new TableCell({ children: [new Paragraph('Created At')] }),
                  ],
                }),
                ...data.sources.map((source) =>
                  new TableRow({
                    children: [
                      new TableCell({ children: [new Paragraph(source.source_id.substring(0, 8) + '...')] }),
                      new TableCell({ children: [new Paragraph(source.name || 'N/A')] }),
                      new TableCell({ children: [new Paragraph(source.source_type || 'N/A')] }),
                      new TableCell({ children: [new Paragraph((source.num_artifacts || 0).toString())] }),
                      new TableCell({ children: [new Paragraph(formatDate(source.created_at))] }),
                    ],
                  })
                ),
              ],
            }),
            new Paragraph({ text: '' }),
          ] : []),

          // Artifacts Detail (renumbered)
          ...(data.artifacts && data.artifacts.length > 0 ? [
            new Paragraph({
              text: '8. Artifacts Detail',
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            }),
            ...data.artifacts.flatMap((artifact, idx) => [
              new Paragraph({
                text: `${idx + 1}. ${artifact.filename || 'Unknown'}`,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Artifact ID: ', bold: true }),
                  new TextRun({ text: artifact.artifact_id }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Type: ', bold: true }),
                  new TextRun({ text: artifact.artifact_type || 'N/A' }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Size: ', bold: true }),
                  new TextRun({ text: formatBytes(artifact.file_size_bytes) }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Chunks: ', bold: true }),
                  new TextRun({ text: (artifact.num_chunks || 0).toString() }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Entities: ', bold: true }),
                  new TextRun({ text: (artifact.num_entities || 0).toString() }),
                ],
              }),
              new Paragraph({ text: '' }),
            ]),
          ] : []),

          // Footer
          new Paragraph({
            text: `Report generated on ${formatDate(new Date().toISOString())}`,
            alignment: AlignmentType.CENTER,
            spacing: { before: 400 },
          }),
        ],
      },
    ],
  });

  // Generate and download
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

